import * as React from 'react';
import { t } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import {
  getCostsSummary,
  listCostCategories,
  costsSummaryCsvUrl,
  type CostsSummaryGroupBy,
  type CostsSummaryRow,
  type CostCategoryDto,
} from '../lib/costs-api.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { fetchCaseAddressIndex } from '../lib/case-address-lookup.js';
import { DomainApiError } from '../lib/api-error.js';
import { DateField } from '../components/date-field.js';

/**
 * `/naklady` - prehled nakladu po nemovitostech/zakazkach/makleri/kategoriich
 * (R5, docs/agent-briefs/REOS-R5-COSTS.md), export CSV. `GET /v1/costs/summary`
 * je agregace (groupBy/from/to), ne surovy seznam - proto NEJDE o
 * `packages/ui/LIST-CONTRACT.md` seznam (page/pageSize/sort tam nedava smysl
 * pro uz-agregovane radky); viz REPORT „co chybi" pro pripadny navazny
 * strankovany seznam jednotlivych nakladu.
 *
 * Nemovitost/zakazka se zobrazi adresou (krok O6a, polozka 2) — id zustava
 * jen jako sekundarni text, kdyz adresa neni znama.
 */

const GROUP_BY_LABELS: Readonly<Record<CostsSummaryGroupBy, string>> = {
  property: 'Nemovitost',
  case: 'Zakázka',
  broker: 'Makléř',
  category: 'Kategorie',
};

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency }).format(value);
}

export function NakladyPage(): React.JSX.Element {
  const [groupBy, setGroupBy] = React.useState<CostsSummaryGroupBy>('property');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [rows, setRows] = React.useState<CostsSummaryRow[] | null>(null);
  const [categories, setCategories] = React.useState<CostCategoryDto[] | null>(null);
  const [members, setMembers] = React.useState<TenantMember[] | null>(null);
  const [addressByPropertyId, setAddressByPropertyId] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [addressByCaseId, setAddressByCaseId] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Adresa (krok O6a) je jen kosmetika — kdyz se nenacte, tabulka porad ukazuje spravna cisla pod id.
  /*
   * Krok APP2 bod 1: tabulky `/naklady-inzerce` a `/neprirazene-naklady` uz
   * index nestahuji cely, ale zuzene na ZNAMA id nemovitosti
   * (`fetchCaseAddressIndex({ propertyIds })`). Tady zustava plny sken
   * ZAMERNE: souhrn se seskupuje i podle ZAKAZKY, takze by potreboval i
   * rozsah `caseIds`, ktery zadani APP2 (bod 23: `/klienti`,
   * `/naklady-inzerce`, `/neprirazene-naklady`, vyber zakazky u nove smlouvy)
   * nejmenuje. Zapsano v „Nálezy" reportu kroku.
   */
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const index = await fetchCaseAddressIndex();
        if (!cancelled) {
          setAddressByPropertyId(index.byPropertyId);
          setAddressByCaseId(index.byCaseId);
        }
      } catch {
        /* adresy nejsou kriticke */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summary, cats, mem] = await Promise.all([
        getCostsSummary({ groupBy, ...(from !== '' ? { from } : {}), ...(to !== '' ? { to } : {}) }),
        listCostCategories(),
        listMembers(),
      ]);
      setRows(summary);
      setCategories(cats);
      setMembers(mem);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : 'Načtení souhrnu nákladů selhalo.');
    } finally {
      setLoading(false);
    }
  }, [groupBy, from, to]);

  React.useEffect(() => {
    void load();
  }, [load]);

  function keyLabel(row: CostsSummaryRow): React.ReactNode {
    if (groupBy === 'category') return categories?.find((c) => c.id === row.key)?.name ?? row.key;
    if (groupBy === 'broker') return members?.find((m) => m.userId === row.key)?.name ?? row.key;
    const address = groupBy === 'property' ? addressByPropertyId.get(row.key) : addressByCaseId.get(row.key);
    if (address === undefined) return row.key;
    return (
      <span className="flex flex-col">
        <span>{address}</span>
        <span className="text-xs text-gray-400">{row.key}</span>
      </span>
    );
  }

  return (
    <div className="p-6" data-testid="naklady-page">
      <h1 className="mb-4 text-xl font-bold">{t('webAdmin.naklady.title', { locale: 'cs' })}</h1>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          {t('webAdmin.naklady.groupBy', { locale: 'cs' })}
          <select
            aria-label={t('webAdmin.naklady.groupByAriaLabel', { locale: 'cs' })}
            className="rh-input block"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as CostsSummaryGroupBy)}
            data-testid="naklady-groupby"
          >
            {(Object.keys(GROUP_BY_LABELS) as CostsSummaryGroupBy[]).map((key) => (
              <option key={key} value={key}>
                {GROUP_BY_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t('webAdmin.naklady.periodFrom', { locale: 'cs' })}
          <DateField ariaLabel={t('webAdmin.naklady.periodFromAriaLabel', { locale: 'cs' })} className="rh-input block" value={from} onValueChange={setFrom} />
        </label>
        <label className="text-sm">
          {t('webAdmin.naklady.periodTo', { locale: 'cs' })}
          <DateField ariaLabel={t('webAdmin.naklady.periodToAriaLabel', { locale: 'cs' })} className="rh-input block" value={to} onValueChange={setTo} />
        </label>
        <a
          className="rh-btn"
          href={costsSummaryCsvUrl({ groupBy, ...(from !== '' ? { from } : {}), ...(to !== '' ? { to } : {}) })}
          target="_blank"
          rel="noopener"
          data-testid="naklady-export-csv"
        >
          {t('webAdmin.naklady.exportCsv', { locale: 'cs' })}
        </a>
      </div>

      {loading && <p role="status">{t('webAdmin.naklady.loading', { locale: 'cs' })}</p>}
      {error !== null && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}

      {!loading && error === null && (rows?.length ?? 0) === 0 ? (
        <EmptyState title={t('webAdmin.naklady.emptyTitle', { locale: 'cs' })} description={t('webAdmin.naklady.emptyDescription', { locale: 'cs' })} />
      ) : (
        <table className="w-full max-w-2xl text-sm" data-testid="naklady-table">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="font-normal">{GROUP_BY_LABELS[groupBy]}</th>
              <th className="font-normal">{t('webAdmin.naklady.total', { locale: 'cs' })}</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((row) => (
              <tr key={`${row.key}-${row.currency}`} data-testid={`naklady-row-${row.key}`}>
                <td>{keyLabel(row)}</td>
                <td>{formatMoney(row.total, row.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
